import React, { useState, useRef } from 'react';
import { playCorrectSound, playWrongSound, playSuccessSound, playKeyPressSound } from '../../utils/audio';
import Confetti from '../ui/Confetti';
import { StarRating } from './GameShell';

interface CheckpointGameProps {
  words: string[];
  rangeLabel: string;
  onContinue: () => void;
}

const CHALLENGE_LENGTH = 10;

const buildChallenge = (pool: string[]): string[] => {
    const clean = Array.from(new Set(pool.map(w => w.trim().toLowerCase()).filter(w => w.length > 0)));
    if (clean.length === 0) return Array(CHALLENGE_LENGTH).fill('keys');

    const list: string[] = [];
    let guard = 0;
    while (list.length < CHALLENGE_LENGTH && guard < 50) {
        guard++;
        const shuffled = [...clean].sort(() => Math.random() - 0.5);
        for (const w of shuffled) {
            if (list.length >= CHALLENGE_LENGTH) break;
            if (list[list.length - 1] === w && clean.length > 1) continue;
            list.push(w);
        }
    }
    return list;
};

const CheckpointGame: React.FC<CheckpointGameProps> = ({ words, rangeLabel, onContinue }) => {
    const [gameState, setGameState] = useState<'intro' | 'playing' | 'results'>('intro');
    const [challenge, setChallenge] = useState<string[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [typedText, setTypedText] = useState('');
    const [correctWords, setCorrectWords] = useState(0);
    const [mistakes, setMistakes] = useState(0);
    const [startTime, setStartTime] = useState<number | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const startGame = () => {
        setChallenge(buildChallenge(words));
        setCurrentIndex(0);
        setTypedText('');
        setCorrectWords(0);
        setMistakes(0);
        setStartTime(Date.now());
        setGameState('playing');
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const currentWord = challenge[currentIndex] || '';

    const submitWord = (raw: string) => {
        const attempt = raw.trim();
        if (!attempt) return;

        if (attempt === currentWord) {
            playCorrectSound();
            setCorrectWords(c => c + 1);
        } else {
            playWrongSound();
            setMistakes(m => m + 1);
        }

        const next = currentIndex + 1;
        setTypedText('');
        if (next >= challenge.length) {
            playSuccessSound();
            setGameState('results');
        } else {
            setCurrentIndex(next);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        playKeyPressSound();
        if (value.endsWith(' ')) {
            submitWord(value);
        } else {
            setTypedText(value);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            submitWord(typedText);
        }
    };

    const renderWord = () => currentWord.split('').map((char, i) => {
        let cls = 'text-gray-400';
        if (i < typedText.length) cls = typedText[i] === char ? 'text-green-400' : 'text-red-400 underline';
        else if (i === typedText.length) cls = 'text-yellow-300 border-b-2 border-yellow-300';
        return <span key={i} className={cls}>{char}</span>;
    });

    if (gameState === 'intro') {
        return (
            <div className="w-full max-w-2xl mx-auto bg-gradient-to-b from-indigo-900 to-slate-900 rounded-xl shadow-lg p-8 text-center text-white">
                <div className="fun-bounce text-6xl mb-4">🚀</div>
                <h2 className="text-3xl font-black mb-2 bg-gradient-to-r from-yellow-300 via-pink-300 to-sky-300 bg-clip-text text-transparent">Checkpoint Launch!</h2>
                <p className="text-indigo-200 mb-1">You just finished {rangeLabel}.</p>
                <p className="text-indigo-300 text-sm mb-6 max-w-md mx-auto">
                    Type the words below to fuel your rocket -- they're pulled straight from the keys and words you just practiced. Get through all {CHALLENGE_LENGTH} to launch!
                </p>
                <button onClick={startGame} className="fun-shine px-10 py-3.5 bg-gradient-to-r from-yellow-300 to-orange-400 text-indigo-900 rounded-full font-black text-xl ring-4 ring-white/30 shadow-lg transition-transform hover:scale-105 active:scale-95">
                    ▶ Start Checkpoint
                </button>
            </div>
        );
    }

    if (gameState === 'results') {
        const elapsedMinutes = startTime ? Math.max((Date.now() - startTime) / 60000, 0.05) : 0.05;
        const wpm = Math.round(correctWords / elapsedMinutes);
        const total = correctWords + mistakes;
        const accuracy = total > 0 ? Math.round((correctWords / total) * 100) : 100;
        const stars = accuracy >= 95 ? 3 : accuracy >= 75 ? 2 : 1;
        const message = stars === 3 ? "Perfect launch! You've mastered this!" : stars === 2 ? "Great job -- solid progress!" : "Nice work -- keep practicing these keys!";

        return (
            <div className="relative w-full max-w-2xl mx-auto bg-gradient-to-b from-indigo-900 to-slate-900 rounded-xl shadow-lg p-8 text-center text-white">
                <Confetti />
                <div className="fun-bounce text-6xl mb-2">{stars === 3 ? '🌟🚀🌟' : '🚀'}</div>
                <h2 className="fun-pop text-3xl font-black mb-1 bg-gradient-to-r from-yellow-300 via-pink-300 to-sky-300 bg-clip-text text-transparent">Checkpoint Complete!</h2>
                <p className="text-indigo-200 mb-2">{message}</p>
                <div className="mb-6"><StarRating stars={stars} /></div>
                <div className="flex justify-center gap-8 mb-8">
                    <div>
                        <div className="text-3xl font-bold text-yellow-300">{accuracy}%</div>
                        <div className="text-xs uppercase text-indigo-300">Accuracy</div>
                    </div>
                    <div>
                        <div className="text-3xl font-bold text-yellow-300">{wpm}</div>
                        <div className="text-xs uppercase text-indigo-300">WPM</div>
                    </div>
                    <div>
                        <div className="text-3xl font-bold text-yellow-300">{correctWords}/{challenge.length}</div>
                        <div className="text-xs uppercase text-indigo-300">Correct</div>
                    </div>
                </div>
                <div className="flex justify-center gap-3">
                    <button onClick={startGame} className="px-5 py-2.5 bg-indigo-700 text-white rounded-full font-bold hover:bg-indigo-600 transition-colors">🔁 Try Again</button>
                    <button onClick={onContinue} className="fun-shine px-7 py-2.5 bg-gradient-to-r from-yellow-300 to-orange-400 text-indigo-900 rounded-full font-black shadow-lg transition-transform hover:scale-105 active:scale-95">Continue Learning →</button>
                </div>
            </div>
        );
    }

    const progress = challenge.length > 0 ? (currentIndex / challenge.length) * 100 : 0;

    return (
        <div className="w-full max-w-2xl mx-auto bg-gradient-to-b from-indigo-900 to-slate-900 rounded-xl shadow-lg p-6 md:p-8 text-white" onClick={() => inputRef.current?.focus()}>
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-indigo-100">🚀 Checkpoint: {rangeLabel}</h2>
                <span className="text-sm text-indigo-300">{currentIndex} / {challenge.length}</span>
            </div>

            <div className="relative h-40 mb-6 flex items-end justify-center">
                <div className="absolute left-1/2 -translate-x-1/2 bottom-0 w-2 h-full bg-indigo-800 rounded-full overflow-hidden">
                    <div className="absolute bottom-0 w-full bg-gradient-to-t from-yellow-400 to-orange-400 transition-all duration-500" style={{ height: `${progress}%` }} />
                </div>
                <div className="relative text-5xl transition-all duration-500" style={{ transform: `translateY(-${progress * 0.9}px)` }}>
                    🚀
                </div>
            </div>

            <div className="p-6 mb-4 bg-black bg-opacity-30 rounded-lg text-center font-mono text-3xl md:text-4xl tracking-wide">
                {renderWord()}
            </div>

            <input
                ref={inputRef}
                type="text"
                value={typedText}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                className="w-full p-3 rounded-lg text-center text-xl font-mono bg-indigo-950 border-2 border-indigo-700 text-white focus:border-yellow-400 focus:outline-none"
                autoFocus
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
            />
        </div>
    );
};

export default CheckpointGame;
